import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { HttpStatusCode } from '@core/enums/http-status-code.enum';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';

/**
 * Handles different types of HTTP errors
 */
export function handleHttpError(
  error: HttpErrorResponse,
  messageService: MessageHandlerService,
  authService: AuthenticationService,
  router: Router
): void {
  if (!(error instanceof HttpErrorResponse)) {
    console.error('Unexpected error occurred:', error);
    messageService.errorMessage(
      'An unexpected error occurred. Please try again.'
    );
    return;
  }

  switch (error.status) {
    case HttpStatusCode.UNAUTHORIZED:
      handleUnauthorizedError(error, messageService, authService);
      break;

    case HttpStatusCode.FORBIDDEN:
      handleForbiddenError(error, messageService, router);
      break;

    case HttpStatusCode.NOT_FOUND:
      handleNotFoundError(error, messageService);
      break;

    case HttpStatusCode.INTERNAL_SERVER_ERROR:
      handleServerError(error, messageService);
      break;

    case HttpStatusCode.BAD_GATEWAY:
    case HttpStatusCode.SERVICE_UNAVAILABLE:
    case HttpStatusCode.GATEWAY_TIMEOUT:
      handleServiceUnavailableError(error, messageService);
      break;

    case 0: // Network/Connection errors
      handleNetworkError(error, messageService);
      break;

    default:
      handleGenericError(error, messageService);
  }
}

function handleUnauthorizedError(
  error: HttpErrorResponse,
  messageService: MessageHandlerService,
  authService: AuthenticationService
): void {
  const message =
    error.error?.error?.message || 'Session expired. Please login again.';
  messageService.errorMessage(message);
  authService.logout();
}

function handleForbiddenError(
  error: HttpErrorResponse,
  messageService: MessageHandlerService,
  router: Router
): void {
  const message =
    error.error?.error?.message ||
    'Access denied. You do not have permission to perform this action.';
  messageService.errorMessage(message);
  router.navigate(['/access-denied']);
}

function handleNotFoundError(
  error: HttpErrorResponse,
  messageService: MessageHandlerService
): void {
  const message =
    error.error?.error?.message || 'The requested resource was not found.';
  messageService.errorMessage(message);
}

function handleServerError(
  error: HttpErrorResponse,
  messageService: MessageHandlerService
): void {
  const message = 'Internal server error. Please try again later.';
  messageService.errorMessage(message);
  console.error('Server error:', error);
}

function handleServiceUnavailableError(
  error: HttpErrorResponse,
  messageService: MessageHandlerService
): void {
  const message = 'Service temporarily unavailable. Please try again later.';
  messageService.errorMessage(message);
}

function handleNetworkError(
  error: HttpErrorResponse,
  messageService: MessageHandlerService
): void {
  const message =
    'Network connection error. Please check your internet connection.';
  messageService.errorMessage(message);
}

function handleGenericError(
  error: HttpErrorResponse,
  messageService: MessageHandlerService
): void {
  const message =
    error.error?.error?.message || 'An error occurred. Please try again.';
  messageService.errorMessage(message);
  console.error('HTTP Error:', error);
}
